import { Skeleton } from "@credit-acceptance/uikit";
import { useTranslation } from "react-i18next";

import { getErrorMessage } from "../../lib/query/errors";
import type { DscChangeHistoryEntry } from "./api/types";
import { useDscNotes } from "./api/useDscNotes";
import AuditTable, { type AuditTableColumn } from "./AuditTable";
import { formatChangeDate } from "./formatChangeDate";

interface PreApprovalChangeHistoryTabProperties {
  approvalId: string;
}

/*
 * DSC Pre-approval Change History tab (OR-21039). Renders the recap-service
 * change-history rows — Date Time / Attribute / Previous Value / Current Value —
 * newest-first (server order), no pagination. caps2 step-6 showed this as a
 * popup; the Workbench renders it as an Internal / Audit drawer tab.
 *
 * Read-only for all six WorkbenchRoles (no role gate). Uses the shared
 * AuditTable component with a sticky header so column labels remain visible
 * on long histories.
 */
function PreApprovalChangeHistoryTab({ approvalId }: PreApprovalChangeHistoryTabProperties) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useDscNotes(approvalId);

  if (isLoading) {
    return (
      <div role="status" aria-label={t("audit.preApprovalHistory.loading")} className="px-1 py-3">
        <Skeleton loading active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-destructive px-1 py-3 text-xs">
        {t("audit.preApprovalHistory.loadError", { message: getErrorMessage(error) })}
      </p>
    );
  }

  const rows = data ?? [];

  if (rows.length === 0) {
    return <p className="text-muted-foreground px-1 py-3 text-xs">{t("audit.preApprovalHistory.empty")}</p>;
  }

  const columns: Array<AuditTableColumn<DscChangeHistoryEntry>> = [
    {
      key: "dateTime",
      header: t("audit.preApprovalHistory.columns.dateTime"),
      render: (row) => <span className="whitespace-nowrap">{formatChangeDate(row.date)}</span>,
    },
    {
      key: "attribute",
      header: t("audit.preApprovalHistory.columns.attribute"),
      render: (row) => row.attributeName,
    },
    {
      key: "previousValue",
      header: t("audit.preApprovalHistory.columns.previousValue"),
      render: (row) => <span className="text-muted-foreground">{row.oldValue}</span>,
    },
    {
      key: "currentValue",
      header: t("audit.preApprovalHistory.columns.currentValue"),
      render: (row) => row.newValue,
    },
  ];

  return <AuditTable caption={t("audit.preApprovalHistory.caption")} columns={columns} rows={rows} stickyHeader />;
}

export default PreApprovalChangeHistoryTab;
